import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = /** @type {string[]} */ ([]);

  #abortController = new AbortController();
  #scrollStartPosition = 0;
  #SCROLL_THRESHOLD_VH = 20;

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    // Close submenu when clicking outside
    document.addEventListener('click', this.#handleOutsideClick, {
      signal: this.#abortController.signal,
    });

    // Close submenu when scrolling more than 20vh
    document.addEventListener('scroll', this.#handleScroll, {
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    // Record scroll position when submenu opens
    this.#scrollStartPosition = document.scrollingElement?.scrollTop ?? 0;

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    // Add/remove class to header-component based on submenu presence
    const headerComponent = /** @type {HTMLElement | null} */ (this.closest('header-component'));
    if (headerComponent) {
      if (submenu) {
        headerComponent.classList.add('has-submenu-open');
        headerComponent.dataset.activeSubmenu = submenu.querySelector('interactive-mega-menu') ? 'interactive' : 'standard';
      } else {
        headerComponent.classList.remove('has-submenu-open');
        delete headerComponent.dataset.activeSubmenu;
      }
    }

    // Mark submenu as active for content-visibility optimization
    if (submenu) {
      submenu.dataset.active = '';
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    // Remove class from header-component when submenu is closed
    const headerComponent = /** @type {HTMLElement | null} */ (this.closest('header-component'));
    if (headerComponent) {
      headerComponent.classList.remove('has-submenu-open');
      delete headerComponent.dataset.activeSubmenu;
    }

    setTimeout(() => {
      item.removeAttribute('data-animating');
      // Remove active state from submenu after animation completes
      if (submenu) {
        delete submenu.dataset.active;
      }
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Handle clicks outside the menu/header to close submenu
   * @param {MouseEvent} event
   */
  #handleOutsideClick = (event) => {
    // Only proceed if there's an active item
    if (!this.#state.activeItem) return;

    // Check if click is outside the header menu and submenu
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Check if click is inside the menu or submenu
    const isInsideMenu = this.contains(target);
    const submenu = findSubmenu(this.#state.activeItem);
    const isInsideSubmenu = submenu ? submenu.contains(target) : false;

    // Also check if click is inside the header component
    const headerComponent = this.closest('header-component');
    const isInsideHeader = headerComponent ? headerComponent.contains(target) : false;

    // If click is outside menu, submenu, AND header, close the submenu
    if (!isInsideMenu && !isInsideSubmenu && !isInsideHeader) {
      this.#debouncedActivateHandler.cancel();
      this.#debouncedDeactivate.cancel();
      this.#deactivate();
    }
  };

  /**
   * Handle scroll events to close submenu after 20vh of scrolling
   */
  #handleScroll = () => {
    // Only proceed if there's an active item
    if (!this.#state.activeItem) return;

    const currentScrollTop = document.scrollingElement?.scrollTop ?? 0;
    const scrollDelta = Math.abs(currentScrollTop - this.#scrollStartPosition);
    const viewportHeight = window.innerHeight;
    const scrollThresholdPx = (this.#SCROLL_THRESHOLD_VH / 100) * viewportHeight;

    // If scrolled more than 20vh, close the submenu
    if (scrollDelta > scrollThresholdPx) {
      this.#debouncedActivateHandler.cancel();
      this.#debouncedDeactivate.cancel();
      this.#deactivate();
    }
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
